import { useEffect, useState } from 'react';
import { ageSeconds, freshnessOf, type Freshness, type HourBucket } from '@dia/db';
import { dictionary, type Lang } from '@dia/i18n';

/**
 * What the Energia screen decides for itself. Everything here is pure except
 * useNow, so it is the same on a phone with no signal and in a test.
 *
 * The one rule behind all of it (Section 8): a value is never shown older than
 * it is. The server hands over a value with the time it was read; the phone
 * ages it by its own clock, corrected for the difference from the server's.
 * Fifteen minutes old is grey with its age. An hour old is "no signal from the
 * island" and the number is not shown at all.
 */

const PANAMA_OFFSET_MS = -5 * 3_600_000;
const DAY = 86_400_000;

export type Reading = {
  /** The number to show, or null when there is nothing honest to show. */
  value: number | null;
  ts: string | null;
  /** 'none' means there never was one. */
  level: Freshness | 'none';
  ageS: number | null;
};

export function readingOf(
  value: number | null | undefined,
  ts: string | null | undefined,
  nowMs: number,
  skewMs: number,
): Reading {
  if (value === null || value === undefined || !ts) {
    return { value: null, ts: ts ?? null, level: 'none', ageS: null };
  }
  const ageS = ageSeconds(ts, nowMs, skewMs);
  const level = freshnessOf(ageS);
  return { value: level === 'silent' ? null : value, ts, level, ageS };
}

/** The Panama calendar day (yyyy-mm-dd) of an instant. */
export function panamaDay(ms: number): string {
  return new Date(ms + PANAMA_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * The hourly chart and the day totals are "today" as of the moment the server
 * worked them out. A copy kept on a phone overnight is yesterday's, and must
 * not be labelled as today's.
 */
export function isToday(computedAt: string, nowMs: number, skewMs: number): boolean {
  return panamaDay(Date.parse(computedAt)) === panamaDay(nowMs + skewMs);
}

const locale = (lang: Lang) => (lang === 'es' ? 'es-PA' : 'en-US');

export function formatPower(watts: number, lang: Lang): string {
  if (Math.abs(watts) < 1000) return `${Math.round(watts)} W`;
  const nf = new Intl.NumberFormat(locale(lang), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${nf.format(watts / 1000)} kW`;
}

export function formatKwh(kwh: number, lang: Lang): string {
  const nf = new Intl.NumberFormat(locale(lang), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${nf.format(kwh)} kWh`;
}

export function formatNumber(value: number, lang: Lang, digits = 1): string {
  return new Intl.NumberFormat(locale(lang), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/** "3 p. m." or "3 PM" for an hour of the day, 0 to 23. */
export function hourText(hour: number, lang: Lang): string {
  return new Intl.DateTimeFormat(locale(lang), { hour: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2000, 0, 1, hour)),
  );
}

/** How a load is named: the dictionary if it knows the slug, the slug made readable if not. */
export function loadName(slug: string, lang: Lang): string {
  const entry = (dictionary as Record<string, { es: string; en: string } | undefined>)[`ops.power.load.${slug}`];
  if (entry) return entry[lang];
  const words = slug.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : slug;
}

/**
 * Bar heights for the by-hour chart, as fractions of a shared ceiling. The
 * ceiling is the biggest hour rounded up to a whole kilowatt (at least 2), so
 * a quiet day does not look like a busy one.
 */
/**
 * An hour is thin when under half of the time that has passed in it has
 * readings. The hour in progress is judged against the part that has passed,
 * not a whole hour: three minutes into one, a single reading is a full set.
 */
export function isThin(b: HourBucket, nowMs: number, skewMs: number): boolean {
  if (b.loadW === null) return false;
  const dayStart = nowMs + skewMs + PANAMA_OFFSET_MS;
  const into = (((dayStart % DAY) + DAY) % DAY) / 3_600_000;
  const elapsed = Math.floor(into) === b.hour ? Math.max(0.05, into - b.hour) : 1;
  return b.coverage < 0.5 * Math.min(1, elapsed);
}

export function chartScale(
  hourly: HourBucket[],
  nowMs: number,
  skewMs: number,
): { ceilingW: number; bars: Array<{ hour: number; fraction: number | null; thin: boolean }> } {
  const peak = hourly.reduce((m, b) => Math.max(m, b.loadW ?? 0), 0);
  const ceilingW = Math.max(2000, Math.ceil(peak / 1000) * 1000);
  return {
    ceilingW,
    bars: hourly.map((b) => ({
      hour: b.hour,
      fraction: b.loadW === null ? null : Math.max(0, Math.min(1, b.loadW / ceilingW)),
      // A partial hour is drawn lighter and said so.
      thin: isThin(b, nowMs, skewMs),
    })),
  };
}

/** The hour to show first: the current one, or the last that has readings. */
export function defaultHour(hourly: HourBucket[], nowMs: number, skewMs: number): number {
  const nowHour = Math.floor((((nowMs + skewMs + PANAMA_OFFSET_MS) % DAY) + DAY) % DAY / 3_600_000);
  if (hourly[nowHour]?.loadW != null) return nowHour;
  for (let h = hourly.length - 1; h >= 0; h -= 1) if (hourly[h]?.loadW != null) return h;
  return nowHour;
}

export type LastRan =
  | { kind: 'days'; days: number }
  /** It was running when the document was made, and we can no longer say it still is. */
  | { kind: 'running_when_seen' }
  /** The island has told us about the generator for a while and has never seen it run. */
  | { kind: 'never_seen'; since: string }
  | { kind: 'none' };

/**
 * What the generator card says about the last time it ran. Counted on the
 * phone's clock from when it was last seen to stop, so a copy kept for a day
 * does not keep saying "2 days". The one thing it must never say is "we have
 * not seen it run" about a generator that was running when the copy was made.
 */
export function lastRan(input: {
  /** When it last ran: the end of the last run, or the document time if it was running. */
  lastRanAt: string | null;
  /** The generator state in the document. */
  documentState: 'running' | 'off' | 'unknown';
  /** The state after the phone has judged the age of its data. */
  state: 'running' | 'off' | 'unknown';
  trackingSince: string | null;
  nowMs: number;
  skewMs: number;
}): LastRan {
  if (input.lastRanAt !== null) {
    if (input.state === 'running') return { kind: 'days', days: 0 };
    if (input.documentState === 'running') return { kind: 'running_when_seen' };
    const days = Math.floor((input.nowMs + input.skewMs - Date.parse(input.lastRanAt)) / DAY);
    return { kind: 'days', days: Math.max(0, days) };
  }
  return input.trackingSince ? { kind: 'never_seen', since: input.trackingSince } : { kind: 'none' };
}

/** A ticking clock for ages that keep counting while the screen is open. */
export function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = () => setNow(Date.now());
    const timer = window.setInterval(tick, everyMs);
    const onWake = () => {
      if (document.visibilityState !== 'hidden') tick();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('online', onWake);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('online', onWake);
    };
  }, [everyMs]);
  return now;
}
